// Room state machine — xstate v5

import { setup, assign } from 'xstate';

export type RoomInfo = Readonly<{
  roomId: string;
  name: string;
  memberCount: number;
  shareCode?: string;
}>;

export type RoomMachineContext = Readonly<{
  rooms: ReadonlyArray<RoomInfo>;
  activeRoomId?: string;
  shareCode?: string;
  errorMessage?: string;
}>;

type RoomEvents =
  | { type: 'CREATE'; name: string }
  | { type: 'JOIN'; shareCode: string }
  | { type: 'CREATE_DONE'; room: RoomInfo; shareCode: string }
  | { type: 'JOIN_DONE'; room: RoomInfo }
  | { type: 'ERROR'; message: string }
  | { type: 'LEAVE' }
  | { type: 'DISMISS' };

export const roomMachine = setup({
  types: {
    context: {} as RoomMachineContext,
    events: {} as RoomEvents,
  },
}).createMachine({
  id: 'room',
  initial: 'idle',
  context: {
    rooms: [],
    activeRoomId: undefined,
    shareCode: undefined,
    errorMessage: undefined,
  },
  states: {
    idle: {
      on: {
        CREATE: { target: 'creating' },
        JOIN: { target: 'joining' },
      },
    },
    creating: {
      on: {
        CREATE_DONE: {
          target: 'active',
          actions: assign({
            rooms: ({ context, event }) => [...context.rooms, event.room],
            activeRoomId: ({ event }) => event.room.roomId,
            shareCode: ({ event }) => event.shareCode,
            errorMessage: () => undefined,
          }),
        },
        ERROR: {
          target: 'error',
          actions: assign({
            errorMessage: ({ event }) => event.message,
          }),
        },
      },
    },
    joining: {
      on: {
        JOIN_DONE: {
          target: 'active',
          actions: assign({
            rooms: ({ context, event }) => [...context.rooms, event.room],
            activeRoomId: ({ event }) => event.room.roomId,
            shareCode: () => undefined,
            errorMessage: () => undefined,
          }),
        },
        ERROR: {
          target: 'error',
          actions: assign({
            errorMessage: ({ event }) => event.message,
          }),
        },
      },
    },
    active: {
      on: {
        LEAVE: {
          target: 'idle',
          actions: assign({
            activeRoomId: () => undefined,
            shareCode: () => undefined,
          }),
        },
        CREATE: { target: 'creating' },
        JOIN: { target: 'joining' },
      },
    },
    error: {
      on: {
        DISMISS: {
          target: 'idle',
          actions: assign({
            errorMessage: () => undefined,
          }),
        },
      },
    },
  },
});
